<template>
  <div v-if="showBanner" class="fixed bottom-0 left-0 right-0 z-50 bg-indigo-600 text-white p-4 shadow-lg md:bottom-4 md:left-4 md:right-auto md:max-w-md md:rounded-lg">
    <button @click="dismiss" class="absolute top-2 right-2 text-white hover:text-gray-200">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>

    <div class="flex items-start space-x-3 pr-6">
      <div class="flex-shrink-0">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
      </div>

      <div class="flex-1">
        <h3 class="font-semibold mb-1">Install App</h3>
        <p class="text-sm text-indigo-100 mb-3">
          {{ bannerMessage }}
        </p>

        <!-- iOS Instructions -->
        <div v-if="deviceInfo.isIOSSafari && !deviceInfo.isPWA" class="space-y-2 mb-3">
          <p class="text-xs font-medium text-indigo-200">How to install:</p>
          <ol class="text-xs text-indigo-100 space-y-1 list-decimal list-inside">
            <li>Tap the <strong>Share</strong> button <svg class="inline w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/></svg></li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong></li>
            <li>Open from your home screen to enable notifications</li>
          </ol>
        </div>

        <!-- Android Install Button -->
        <button
          v-else-if="canInstall"
          @click="install"
          class="bg-white text-indigo-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-50 transition-colors"
        >
          Install Now
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import {
  isPWA,
  isIOSSafari,
  isAndroid,
  iosSupportsWebPush,
  shouldShowInstallBanner,
  dismissInstallBanner,
  setupPWAInstallPrompt,
  triggerPWAInstall,
  getDeviceInfo
} from '../utils/pwa';

export default {
  name: 'PWAInstallBanner',

  setup() {
    const showBanner = ref(false);
    const canInstall = ref(false);
    const deviceInfo = ref({});

    const bannerMessage = computed(() => {
      if (deviceInfo.value.isIOSSafari) {
        if (!deviceInfo.value.iosSupportsWebPush) {
          return 'Install this app for a better experience. Note: Push notifications require iOS 16.4+';
        }
        return 'Install this app to receive push notifications and use it like a native app!';
      }
      return 'Install this app for a better experience with push notifications!';
    });

    const checkAndShowBanner = () => {
      deviceInfo.value = getDeviceInfo();

      if (shouldShowInstallBanner()) {
        showBanner.value = true;
      }
    };

    const dismiss = () => {
      showBanner.value = false;
      dismissInstallBanner();
    };

    const install = async () => {
      const result = await triggerPWAInstall();
      if (result.success) {
        showBanner.value = false;
      }
    };

    onMounted(() => {
      // Setup install prompt listener
      setupPWAInstallPrompt((installable) => {
        canInstall.value = installable;
        checkAndShowBanner();
      });

      // Check if should show banner
      setTimeout(checkAndShowBanner, 2000); // Show after 2 seconds
    });

    return {
      showBanner,
      canInstall,
      deviceInfo,
      bannerMessage,
      dismiss,
      install
    };
  }
};
</script>

